<template>
  <div class="album-tab" :lang="$domLang">
    <!-- 본문 -->
    <v-sheet class="pt-4 mx-auto">
      <!-- 불러오는 중 / 실패 / 없음 (예약 내역 탭과 같은 모양) -->
      <section v-if="!list.length" class="album-state" role="status" aria-live="polite">
        <template v-if="loadState === 'loading'">
          <v-progress-circular indeterminate size="28" width="3" color="#d74369" />
          <p class="album-state__text">{{ $t("page.mypage.album.loading") }}</p>
        </template>
        <template v-else-if="loadState === 'error'">
          <p class="album-state__text">{{ $t("page.mypage.album.load-failed") }}</p>
          <v-btn depressed class="album-state__button" @click="load">{{ $t("page.mypage.reserv.reload-button") }}</v-btn>
        </template>
        <p v-else class="album-state__text">{{ $t("page.mypage.album.empty") }}</p>
      </section>

      <!-- 내 사진 내역 있을 시 -->
      <v-sheet v-if="list.length" class="py-1">
        <v-row no-gutters>
          <v-col v-for="item in list" :key="`${item.source_type || 'makershot'}-${item.id}`" cols="4" style="border: 1px solid white; cursor: pointer">
            <v-img
              width="100%"
              aspect-ratio="0.8"
              :src="item.thumbnailUrl"
              @click="openDownload(item)"
              :gradient="item.completeStatus == '완료예정' ? 'rgba(92,92,92,.8),rgba(92,92,92,.8)' : ''"
              :style="item.completeStatus === '완료' ? 'cursor: cursor' : 'cursor: default'"
            >
              <div class="sela-bottom-caption d-flex justify-center align-center" :class="item.status != 2 ? 'disabled' : ''">
                <div style="color: white; position: absolute; bottom: 8px; font-size: 0.8rem; text-align: center">
                  {{ getTitle(item.title) }}
                  <br />
                  {{ getDispText(item.completeStatus) }}
                </div>
              </div>
            </v-img>
          </v-col>
        </v-row>
      </v-sheet>
    </v-sheet>
  </div>
</template>
<script>
import { fetchMyPictureItemList } from "@/models/MyPicture"
import { mapState } from "vuex"
import { mayUseEnhancedMyPage } from "@/util/mypageCanary"
import additionalCanaryEmails from "@/config/mypageCanaryAccounts.json"
import mypageRelease from "@/config/mypageRelease.json"

export default {
  data: () => ({
    list: [],
    loadState: "loading",
  }),

  mounted() {
    this.load()
  },

  computed: {
    ...mapState(["lang", "concepts"]),
    isEnhancedMyPage() {
      const canaryEmails = [
        process.env.VUE_APP_MYPAGE_CANARY_EMAILS,
        ...additionalCanaryEmails,
      ]
        .filter(Boolean)
        .join(",")

      return mayUseEnhancedMyPage({
        email: this.$store.state.lafia_user && this.$store.state.lafia_user.email,
        runtimeProfile: process.env.VUE_APP_RUNTIME_PROFILE,
        canaryEmails,
        publicRelease: mypageRelease.public,
      })
    },
  },

  methods: {
    async load() {
      if (!this.list.length) this.loadState = "loading"
      try {
        await this.fetchPictures()
        this.loadState = "ready"
      } catch (error) {
        console.error("Error loading album:", error)
        this.loadState = "error"
      }
    },

    async fetchPictures() {
      const legacyRequest = fetchMyPictureItemList(this, this.$store.state.lafia_user.user_id)
      const premiumRequest = this.isEnhancedMyPage
        ? this.$http.get("/api/photo-deliveries/completed").then((response) => response.data).catch(() => [])
        : Promise.resolve([])
      const [{ data: legacyPictures }, premiumPictures] = await Promise.all([legacyRequest, premiumRequest])
      const normalizedPremiumPictures = premiumPictures.map((item) => ({
        ...item,
        thumbnailUrl: item.thumbnail_url,
        imageUrl: item.complete_url,
        processStatus: "complete",
        completeStatus: "완료",
        status: 2,
        source_type: "premium",
      }))

      this.list = [...normalizedPremiumPictures, ...legacyPictures.reverse()]
    },

    openDownload(item) {
      if (item.processStatus == "effect") {
        // TODO: 후보정 안내문구 팝업 필요?
        return
      }
      if (item.completeStatus !== "완료") {
        return
      }

      this.$router.push(`/mypage/picDetail/${item.id}`)
    },

    // 사진 상태값(완료·완료예정)은 서버 데이터라 한국어로 오며, 화면에는 번역해서 보인다
    getDispText(status) {
      if (status === "완료") return this.$t("page.mypage.album.status-done")
      if (status === "완료예정") return this.$t("page.mypage.album.status-progress")
      return this.$t("page.mypage.album.status-preparing")
    },
    getTitle(conceptTitleKr) {
      const concept = this.concepts.find((c) => c.title === conceptTitleKr)
      switch (this.lang) {
        case "en":
          return (concept && concept.title_en) || conceptTitleKr
          break
        case "jp":
          return (concept && concept.title_jp) || conceptTitleKr

        default:
          return conceptTitleKr
      }
    },
  },
}
</script>
<style scoped>
.album-tab {
  background: #fffdf9;
  font-family: "NanumSquare", sans-serif;
}
.album-tab:lang(ja) {
  font-family: "Noto Sans JP", sans-serif;
}
.album-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 12px 16px 24px;
  padding: 26px 18px;
  color: #6b4b47;
  background: #fff9ee;
  border-radius: 22px;
  text-align: center;
}
.album-state__text {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.6;
}
.album-state__button.v-btn {
  margin-top: 14px;
  border-radius: 7px;
  background: #d74369 !important;
  color: #fff !important;
  font-weight: 700;
  letter-spacing: 0;
}
</style>
